import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { challenges as seedChallenges, clubs as seedClubs, communities as seedCommunities, posts as seedPosts, socialAuthors, stories as seedStories } from '../data/social';
import { extractMentions } from '../utils/social';
import type { Audience, Challenge, Club, PostKind, SocialCommunity, SocialPost, Story } from '../types/social';

/** The account acting on this device, as a social author. */
const me = {
  id: 'CAMP-2AB19XQ7',
  kind: 'student' as const,
  name: 'Saviour Adeyemi',
  handle: 'saviour',
  initials: 'SA',
  context: '300L Mechanical Engineering',
  verified: true
};
interface SocialValue {
  me: typeof me;
  posts: SocialPost[];
  communities: SocialCommunity[];
  clubs: Club[];
  challenges: Challenge[];
  stories: Story[];
  interests: string[];
  hidden: string[];
  publish: (input: {
    kind: PostKind;
    body: string;
    audience: Audience;
    spaceId: string | null;
    pollOptions?: string[];
  }) => void;
  toggleReaction: (postId: string) => void;
  toggleSave: (postId: string) => void;
  comment: (postId: string, body: string, parentId: string | null) => void;
  answer: (postId: string, body: string) => void;
  resolve: (postId: string) => void;
  vote: (postId: string, optionId: string) => void;
  lockComments: (postId: string, locked: boolean) => void;
  hide: (postId: string) => void;
  toggleCommunity: (communityId: string) => void;
  toggleFollowCommunity: (communityId: string) => void;
  toggleClub: (clubId: string) => void;
  toggleChallenge: (challengeId: string) => void;
  markStorySeen: (storyId: string) => void;
  toggleInterest: (interest: string) => void;
}
const Ctx = createContext<SocialValue | null>(null);
export function SocialProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [posts, setPosts] = useState<SocialPost[]>(seedPosts);
  const [communities, setCommunities] = useState<SocialCommunity[]>(seedCommunities);
  const [clubs, setClubs] = useState<Club[]>(seedClubs);
  const [challenges, setChallenges] = useState<Challenge[]>(seedChallenges);
  const [stories, setStories] = useState<Story[]>(seedStories);
  const [interests, setInterests] = useState<string[]>(['Engineering', 'Photography', 'Entrepreneurship']);
  const [hidden, setHidden] = useState<string[]>([]);
  const patch = useCallback((postId: string, next: (post: SocialPost) => SocialPost) => {
    setPosts(current => current.map(post => post.id === postId ? next(post) : post));
  }, []);
  const publish = useCallback<SocialValue['publish']>(input => {
    const now = new Date().toISOString();
    const post: SocialPost = {
      id: `pst-${Date.now()}`,
      kind: input.kind,
      author: me,
      body: input.body,
      at: now,
      audience: input.audience,
      audienceLabel: input.spaceId !== null ? 'Members of the space you posted into' : input.audience === 'campus' ? 'FUTA Akure campus' : 'Your connections',
      spaceId: input.spaceId,
      campusId: 'cmp-futa-main',
      state: 'published',
      reactions: 0,
      myReaction: false,
      saved: false,
      comments: [],
      commentsLocked: false,
      poll: input.kind === 'poll' && input.pollOptions ? {
        question: input.body,
        options: input.pollOptions.filter(option => option.trim()).map((option, index) => ({
          id: `opt-new-${index}`,
          label: option.trim(),
          votes: 0
        })),
        closesAt: new Date(Date.now() + 3 * 24 * 3600 * 1000).toISOString(),
        anonymous: true,
        myVote: null,
        eligibility: 'One vote per account.'
      } : null,
      answers: [],
      resolved: false,
      attached: null,
      imagePrompt: null,
      reason: 'You posted this.',
      removalReason: null
    };
    setPosts(current => [post, ...current]);
  }, []);
  const toggleReaction = useCallback<SocialValue['toggleReaction']>(postId => {
    patch(postId, post => ({
      ...post,
      myReaction: !post.myReaction,
      reactions: post.reactions + (post.myReaction ? -1 : 1)
    }));
  }, [patch]);
  const toggleSave = useCallback<SocialValue['toggleSave']>(postId => patch(postId, post => ({
    ...post,
    saved: !post.saved
  })), [patch]);
  const comment = useCallback<SocialValue['comment']>((postId, body, parentId) => {
    patch(postId, post => ({
      ...post,
      comments: [...post.comments, {
        id: `cmt-${Date.now()}`,
        author: me,
        body,
        at: new Date().toISOString(),
        parentId,
        mentions: extractMentions(body),
        removed: false
      }]
    }));
  }, [patch]);
  const answer = useCallback<SocialValue['answer']>((postId, body) => {
    patch(postId, post => ({
      ...post,
      answers: [...post.answers, {
        id: `ans-${Date.now()}`,
        author: me,
        body,
        at: new Date().toISOString(),
        // A student answer is never automatically verified.
        verified: false,
        aiGenerated: false,
        helpful: 0
      }]
    }));
  }, [patch]);
  const resolve = useCallback<SocialValue['resolve']>(postId => patch(postId, post => ({
    ...post,
    resolved: true
  })), [patch]);
  const vote = useCallback<SocialValue['vote']>((postId, optionId) => {
    patch(postId, post => {
      // One vote per identity: refuse rather than silently re-counting.
      if (!post.poll || post.poll.myVote !== null) return post;
      return {
        ...post,
        poll: {
          ...post.poll,
          myVote: optionId,
          options: post.poll.options.map(option => option.id === optionId ? {
            ...option,
            votes: option.votes + 1
          } : option)
        }
      };
    });
  }, [patch]);
  const lockComments = useCallback<SocialValue['lockComments']>((postId, locked) => patch(postId, post => ({
    ...post,
    commentsLocked: locked
  })), [patch]);
  const hide = useCallback<SocialValue['hide']>(postId => {
    setHidden(current => [...current, postId]);
  }, []);
  const toggleCommunity = useCallback<SocialValue['toggleCommunity']>(communityId => {
    setCommunities(current => current.map(community => community.id === communityId ? {
      ...community,
      joined: !community.joined
    } : community));
  }, []);
  const toggleFollowCommunity = useCallback<SocialValue['toggleFollowCommunity']>(communityId => {
    setCommunities(current => current.map(community => community.id === communityId ? {
      ...community,
      following: !community.following
    } : community));
  }, []);
  const toggleClub = useCallback<SocialValue['toggleClub']>(clubId => {
    setClubs(current => current.map(club => club.id === clubId ? {
      ...club,
      joined: !club.joined,
      myRole: club.joined ? null : 'Member'
    } : club));
  }, []);
  const toggleChallenge = useCallback<SocialValue['toggleChallenge']>(challengeId => {
    setChallenges(current => current.map(challenge => challenge.id === challengeId ? {
      ...challenge,
      registered: !challenge.registered,
      participants: challenge.participants + (challenge.registered ? -1 : 1)
    } : challenge));
  }, []);
  const markStorySeen = useCallback<SocialValue['markStorySeen']>(storyId => {
    setStories(current => current.map(story => story.id === storyId ? {
      ...story,
      seen: true
    } : story));
  }, []);
  const toggleInterest = useCallback<SocialValue['toggleInterest']>(interest => {
    setInterests(current => current.includes(interest) ? current.filter(item => item !== interest) : [...current, interest]);
  }, []);
  const value = useMemo<SocialValue>(() => ({
    me,
    posts,
    communities,
    clubs,
    challenges,
    stories,
    interests,
    hidden,
    publish,
    toggleReaction,
    toggleSave,
    comment,
    answer,
    resolve,
    vote,
    lockComments,
    hide,
    toggleCommunity,
    toggleFollowCommunity,
    toggleClub,
    toggleChallenge,
    markStorySeen,
    toggleInterest
  }), [posts, communities, clubs, challenges, stories, interests, hidden, publish, toggleReaction, toggleSave, comment, answer, resolve, vote, lockComments, hide, toggleCommunity, toggleFollowCommunity, toggleClub, toggleChallenge, markStorySeen, toggleInterest]);
  return <Ctx.Provider value={value} data-id="element-5540" data-mp="3242ahyx5bmk3">{children}</Ctx.Provider>;
}
export function useSocial(): SocialValue {
  const value = useContext(Ctx);
  if (!value) throw new Error('useSocial must be used inside SocialProvider');
  return value;
}
export { socialAuthors };